import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { ArrowsClockwise, Plugs, Envelope, Robot, CalendarBlank } from '@phosphor-icons/react';
import { format } from 'date-fns';
import {
  PriorityCard,
  AIBriefing,
  QuickStats,
  WeekOverview,
  AIRecommendations,
  SubscriptionInsights,
  WeeklyVideo,
  HeroBriefing,
  DailyQuote,
} from '../components/dashboard';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem('onboarding_dismissed') === 'true'
  );

  useEffect(() => {
    fetchDashboard();
    checkGmailStatus();

    // Re-fetch when user navigates back to this tab/page (catches task updates from Inbox/Assistant)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboard();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Re-fetch when navigating back to dashboard from other pages
  useEffect(() => {
    if (data) fetchDashboard();
  }, [location.key]);

  const checkGmailStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gmail/status`, { withCredentials: true });
      setGmailConnected(res.data.connected);
    } catch { setGmailConnected(false); }
  };

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem('onboarding_dismissed', 'true');
  };

  const handleReviewSubscription = async (provider) => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/review`, { provider }, { withCredentials: true });
      setData(prev => {
        if (!prev?.subscription_insights) return prev;
        return {
          ...prev,
          subscription_insights: {
            ...prev.subscription_insights,
            subscriptions: (prev.subscription_insights.subscriptions || []).filter(s => s.provider !== provider),
          }
        };
      });
    } catch (err) {
      console.error('Review error:', err);
    }
  };

  const pollForRefresh = async () => {
    setRefreshing(true);
    for (let i = 0; i < 40; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const status = await axios.get(`${API_URL}/api/dashboard/refresh-status`, { withCredentials: true });
        if (!status.data.refreshing) {
          const res = await axios.get(`${API_URL}/api/dashboard`, { withCredentials: true });
          setData(res.data);
          setRefreshing(false);
          return;
        }
      } catch { /* continue */ }
    }
    // Timeout fallback
    try {
      const res = await axios.get(`${API_URL}/api/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch { /* ignore */ }
    setRefreshing(false);
  };

  const fetchDashboard = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
        await axios.post(`${API_URL}/api/dashboard/refresh`, {}, { withCredentials: true });
        pollForRefresh();
      } else {
        const res = await axios.get(`${API_URL}/api/dashboard`, { withCredentials: true });
        setData(res.data);
        // If backend is refreshing in background (e.g. after login), start polling
        if (res.data.refreshing) {
          pollForRefresh();
        }
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setRefreshing(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4 lg:p-8 space-y-5">
          <div className="animate-pulse space-y-5">
            <div className="h-10 w-64 bg-[#f2f4f6] rounded-xl" />
            <div className="h-64 bg-[#f2f4f6] rounded-2xl" />
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#f2f4f6] rounded-xl" />)}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const ai = data?.ai_briefing || {};
  const topPriority = data?.top_priority;
  const week = data?.week_overview || {};
  const recs = data?.recommendations || [];
  const stats = data?.quick_stats || {};
  const upcoming = data?.upcoming_tasks || [];
  const subInsights = data?.subscription_insights || {};
  const briefingImage = data?.briefing_image || null;

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2d3338] tracking-tight" data-testid="dashboard-title">
              {data?.user?.name ? `Hey, ${data.user.name}` : 'Dashboard'}
            </h1>
            <p className="text-xs text-[#596065] mt-0.5">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <button
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              refreshing
                ? 'bg-[#f2f4f6] text-[#acb3b8] cursor-not-allowed'
                : 'bg-[#f0f7ff] text-[#005bc1] hover:bg-[#d6e3ff]'
            }`}
            data-testid="refresh-dashboard"
          >
            <ArrowsClockwise size={14} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh AI'}
          </button>
        </div>

        {/* Onboarding */}
        {gmailConnected === false && !onboardingDismissed && (
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#001b3d] to-[#003175] p-5 lg:p-6" data-testid="onboarding-banner">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#005bc1] rounded-full opacity-10 -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#005bc1] flex items-center justify-center">
                    <Plugs size={20} className="text-white" weight="duotone" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-base">Get started in 2 minutes</h2>
                    <p className="text-[#93c5fd] text-xs">Connect your email and let AI handle the rest</p>
                  </div>
                </div>
                <button onClick={dismissOnboarding} className="text-[#93c5fd] hover:text-white text-xs transition-colors" data-testid="dismiss-onboarding">Dismiss</button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full bg-[#005bc1] text-white text-[10px] font-bold flex items-center justify-center">1</span>
                    <span className="text-white font-semibold text-xs">Connect Gmail</span>
                  </div>
                  <p className="text-[#93c5fd] text-[10px] mb-3">We'll scan for bills, subscriptions, and appointments.</p>
                  <a href="/connections" className="inline-flex items-center gap-1.5 bg-white text-[#001b3d] px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#f0f7ff] transition-colors" data-testid="onboarding-connect-gmail">
                    <Envelope size={14} weight="duotone" /> Connect Gmail
                  </a>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 opacity-70">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">2</span>
                    <span className="text-white font-semibold text-xs">Scan & Extract</span>
                  </div>
                  <p className="text-[#93c5fd] text-[10px] mb-3">AI reads emails, detects bills & subscriptions status.</p>
                  <span className="inline-flex items-center gap-1.5 text-[#93c5fd] text-[10px]"><Robot size={12} /> Auto after connect</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 opacity-70">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">3</span>
                    <span className="text-white font-semibold text-xs">Sync Calendar</span>
                  </div>
                  <p className="text-[#93c5fd] text-[10px] mb-3">Google Calendar events alongside tasks in one view.</p>
                  <span className="inline-flex items-center gap-1.5 text-[#93c5fd] text-[10px]"><CalendarBlank size={12} /> Included with Gmail</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Personalised daily quote — sits right above the hero image so the
            user sees a warm, tailored line before scanning their day. */}
        <DailyQuote />

        {/* Hero: Image + Do This First + Briefing Text — unified row */}
        <HeroBriefing ai={ai} briefingImage={briefingImage} topPriority={topPriority} refreshing={refreshing} cachedAt={data?.cached_at} />

        {/* Quick Stats */}
        <QuickStats stats={stats} />

        {/* Week Overview + AI Recommendations */}
        <div className="grid lg:grid-cols-5 gap-5">
          <WeekOverview week={week} upcoming={upcoming} />
          <AIRecommendations recs={recs} />
        </div>

        {/* Weekly Video Summary */}
        <WeeklyVideo />

        {/* Subscription Insights */}
        {subInsights.has_data && (
          <SubscriptionInsights data={subInsights} onReview={handleReviewSubscription} />
        )}
      </div>
    </Layout>
  );
}
